import { api } from '../api/client';
import { attentionCacheFresh } from './attentionView';

/**
 * UX-1 — the module-level shared-fetch store behind the ONE «کارهای امروز» worklist strip, extracted from
 * <AttentionStrip> so the security-critical caching logic is a testable authority in its own right (the
 * component renders it; this owns the scan). Deals↔cash reuse ONE /api/attention scan via a short TTL, and an
 * accountability action force-refetches + notifies every mounted strip.
 *
 * OWNER-AWARE (the UX-1 defect fix): login/logout is an SPA state change with NO page reload
 * (AuthProvider.tsx), so this module state outlives the auth swap — a plain shared cache would serve User A's
 * money-ranked, server-`dashboard.read`-gated worklist to whoever logs in next on the same browser, before B
 * ever calls (and is possibly 403'd by) /api/attention. So the cache is tagged with the username that fetched
 * it: `attentionSnapshot(owner)` returns it ONLY to that same session (a different / logged-out user
 * synchronously sees `null`, even on the very first render), and a session change drops it. This mirrors
 * NotificationProvider's logout reset, adapted for a store that outlives the components that read it — the
 * `dashboard.read` gate is never weakened, only prevented from being bypassed client-side.
 */

/** Mirrors `AttentionItem` in packages/server/src/services/attention.ts — the money-ranked "what needs me
 *  today". Already sorted worst-first (by `score`) server-side. */
export interface AttentionItem {
  id: string;
  kind:
    | 'cash_shortfall'
    | 'over_credit'
    | 'overdue_tranche'
    | 'margin_at_risk'
    | 'below_floor'
    /** BIDX-P7 — NON-SNOOZABLE server-side (attention.ts NON_SNOOZABLE_PREFIXES); the strip must not offer
     *  a snooze button for it, and POST /api/attention/snooze rejects its id with a 400. */
    | 'unpriced_receipt';
  severity: 'critical' | 'warning' | 'info';
  rialsAtStake: number;
  dueDays: number | null;
  title: string;
  body: string;
  target: { view: string; focusKind?: 'deal' | 'customer'; focusId?: number };
  score: number;
  /** team accountability (CW-12): present when a teammate has claimed this item. Snoozed items don't appear. */
  ack?: { actor: string };
}

// ---- The shared store. `cacheOwner` is the username whose scan `cache` holds (null = empty); `generation`
// bumps on every reset so a fetch that was already in flight across a session change can't write stale data
// back in. The TTL rule + constant live in ./attentionView so the store can't drift from what's pinned. ----
let cache: AttentionItem[] | null = null;
let fetchedAt = 0;
let cacheOwner: string | null = null;
let generation = 0;
let inflight: Promise<AttentionItem[]> | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

export function subscribeAttention(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** Owner-aware snapshot: hand back the cached scan ONLY to the session that fetched it. A different user — or
 *  a logged-out one (`owner === null`) — synchronously gets `null`, so a stale, server-gated worklist can
 *  never paint for the next user on the same browser, not even for the first frame before the reload effect
 *  fires. This is the client-side half of the `dashboard.read` gate. */
export function attentionSnapshot(owner: string | null): AttentionItem[] | null {
  return owner !== null && cacheOwner === owner ? cache : null;
}

/** Drop the shared store (session changed / logged out). Bumps `generation` so any in-flight fetch from the
 *  previous owner is discarded on resolve instead of repopulating the cache. */
export function resetAttentionStore(): void {
  generation++;
  cache = null;
  fetchedAt = 0;
  cacheOwner = null;
  inflight = null;
  emit();
}

async function fetchAttention(owner: string): Promise<AttentionItem[]> {
  const gen = generation;
  const r = await api<{ items?: AttentionItem[] }>('/api/attention');
  // Best-effort: a hiccup never blocks the host screen — degrade to an empty (silent) worklist.
  const items =
    r.ok && Array.isArray((r.body as { items?: AttentionItem[] }).items)
      ? (r.body as { items: AttentionItem[] }).items
      : [];
  // A session change (reset) happened while this was in flight — discard rather than serve the wrong user.
  if (gen !== generation) return items;
  cache = items;
  cacheOwner = owner;
  fetchedAt = Date.now();
  emit();
  return items;
}

export function loadAttention(owner: string, force = false): Promise<AttentionItem[]> {
  // Session changed under the shared store: drop the previous owner's scan before fetching ours, so its
  // cache/inflight is never reused as if it were this user's.
  if (cacheOwner !== null && cacheOwner !== owner) resetAttentionStore();
  const mine = cacheOwner === owner;
  if (!force && mine && cache && attentionCacheFresh(fetchedAt, Date.now()))
    return Promise.resolve(cache);
  if (!force && mine && inflight) return inflight;
  const p = fetchAttention(owner).finally(() => {
    if (inflight === p) inflight = null;
  });
  inflight = p;
  return p;
}

/** Team accountability (CW-12): claim / snooze / un-mark, then FORCE a re-read so every mounted strip (this
 *  screen and any other) reflects it — a snoozed item drops out, an acked one shows who's on it. */
export async function actOnAttention(
  owner: string,
  id: string,
  action: 'ack' | 'snooze' | 'clear',
  days?: number,
): Promise<void> {
  const body = JSON.stringify(action === 'snooze' ? { id, days } : { id });
  await api(`/api/attention/${action}`, { method: 'POST', body });
  await loadAttention(owner, true);
}
